/**
 * "Verify your mobile number" — the step between a signed-in donor and a check-in (GH-883).
 *
 * Owner, 2026-10-04: "Door Prize should be tied to check-into event and both require mobile number
 * sms verification if not already done in profile."
 *
 * The server decides when this appears: `POST /v1/donor/check-ins` answers
 * `phone_verification_required` only where a code can actually be sent and only to a profile with
 * no verified number. A donor who signed in with their phone never sees it; a donor who signed in
 * with Apple or Google sees it once, and the number then lives on their profile.
 *
 * No second verification mechanism: this is the profile's own add-a-number pair
 * (`/v1/donor/phone` + `/confirm`), drawn with the sign-in's own `PhoneStep` and `CodeStep` so the
 * SMS autofill and the six-digit rule are the ones every other code uses. The server throttles
 * sends and caps attempts; the code is never stored here.
 */
import { useState } from "react";
import {
  confirmProfilePhoneVerification,
  DonorApiError,
  startProfilePhoneVerification,
} from "./donor-session.js";
import { CodeStep, VerifyMobilePhoneStep } from "./SignInSteps.js";

/**
 * The server's "verify your mobile first" (GH-883), from check-in or a door-prize entry. Read off
 * the error's code rather than its class, so it stays a pure function wherever it is called from.
 */
export const isPhoneVerificationRequired = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { code?: unknown }).code === "phone_verification_required";

type Step =
  { kind: "phone" } | { kind: "code"; phone: string; verificationId: string; hint: string | null };

export function VerifyMobileStep({
  token,
  headingId = "donor-title",
  onVerified,
}: {
  token: string;
  headingId?: string;
  /** Called once the number is on the profile; the host retries what was refused. */
  onVerified: () => void;
}) {
  const [step, setStep] = useState<Step>({ kind: "phone" });
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const failWith = (caught: unknown, fallback: string) =>
    setFailure(caught instanceof DonorApiError ? caught.message : fallback);

  return (
    <section aria-live="polite">
      {step.kind === "phone" ? (
        <VerifyMobilePhoneStep
          busy={busy}
          headingId={headingId}
          onSubmit={(phone) => {
            setFailure(null);
            setBusy(true);
            startProfilePhoneVerification(token, phone)
              .then((started) =>
                setStep({
                  kind: "code",
                  phone,
                  verificationId: started.verificationId,
                  hint: started.developmentCode,
                }),
              )
              .catch((caught: unknown) => failWith(caught, "Could not send a code."))
              .finally(() => setBusy(false));
          }}
        />
      ) : (
        <>
          <CodeStep
            hint={step.hint}
            busy={busy}
            headingId={headingId}
            onSubmit={(code) => {
              setFailure(null);
              setBusy(true);
              confirmProfilePhoneVerification(token, {
                verificationId: step.verificationId,
                code,
                phone: step.phone,
              })
                .then(() => onVerified())
                .catch((caught: unknown) => failWith(caught, "That code is not correct."))
                .finally(() => setBusy(false));
            }}
          />
          <button
            type="button"
            className="link landing__quiet"
            onClick={() => {
              setFailure(null);
              setStep({ kind: "phone" });
            }}
          >
            Use a different number
          </button>
        </>
      )}
      {failure !== null && (
        <p className="notice notice--error" role="alert">
          {failure}
        </p>
      )}
    </section>
  );
}
